import { useMutation } from "@tanstack/react-query";
import type { ClubBook, MineState } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { StarPicker } from "./Stars";
import { ProgressEditor } from "./ProgressEditor";
import { pageAt } from "../lib/format";
import { reading } from "@pagemates/shared/copy";

const txt = reading.mineBlock;

/**
 * You, on a finished book: I've read it, Reading it now (with the full
 * progress editor), or Didn't read it; and your rating underneath when
 * you've read it or are reading it. The recap page's second block.
 */
export function MineBlock({ clubId, book, onChange }: { clubId: string; book: ClubBook; onChange: () => Promise<unknown> }) {
  const mine = book.mine;
  const r = book.ratings;
  const setMine = useMutation({ mutationFn: (state: MineState) => api.setMine(clubId, book.id, state), onSuccess: onChange });
  const rate = useMutation({ mutationFn: (stars: number) => api.rateBook(clubId, book.id, stars), onSuccess: onChange });
  const clear = useMutation({ mutationFn: () => api.clearRating(clubId, book.id), onSuccess: onChange });
  if (!mine) return null;
  const state: MineState = !mine.touched ? "unread" : mine.status === "finished" ? "read" : "reading";
  const where = state === "reading" && mine.percent > 0 ? `${book.book.pages ? txt.pOf(pageAt(mine.percent, book.book.pages), book.book.pages) : ""}${mine.percent}%` : null;
  const err = setMine.error ?? rate.error ?? clear.error;
  return (
    <div className="you mine">
      <div className="sect"><h3>{txt.you}</h3>{where && <span className="count">{where}</span>}</div>
      <div className="seg three" role="radiogroup" aria-label={txt.didYouReadIt}>
        <button type="button" aria-pressed={state === "read"} disabled={setMine.isPending} onClick={() => setMine.mutate("read")}>{txt.iVeReadIt}</button>
        <button type="button" aria-pressed={state === "reading"} disabled={setMine.isPending} onClick={() => setMine.mutate("reading")}>{txt.readingItNow}</button>
        <button type="button" aria-pressed={state === "unread"} disabled={setMine.isPending} onClick={() => setMine.mutate("unread")}>{txt.didnTReadIt}</button>
      </div>
      {state === "reading" && <ProgressEditor clubId={clubId} book={book} percent={mine.percent} myFormat={mine.format} source={null} done={false} onSaved={() => void onChange()} />}
      {state !== "unread" && r && (
        <div className="rate">
          <StarPicker value={r.mine} disabled={rate.isPending} onChange={(v) => rate.mutate(v)} />
          {r.mine !== null ? <button type="button" className="linkish small muted d" onClick={() => clear.mutate()} disabled={clear.isPending}>{txt.yourRatingClear}</button> : <span className="muted small d">{txt.yourRating}</span>}
        </div>
      )}
      {err && <p className="error small">{describeError(err)}</p>}
    </div>
  );
}
